import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { useState } from "react";
import advancedFormat from "dayjs/plugin/advancedFormat";

import useFetchNotifications, {
  NotificationRecord,
} from "../../../api/api-hooks/notifications/useFetchNotifications";
import BasicTableView from "../../supportal/CloudListView/BasicTableView";
import { Query } from "../../../types/GlobalInterfaces";
import BasicToolbar from "./BasicToolbar";
import AppTablePagination from "../../../components/AppTable/AppTablePagination";
import useModule from "../../../hooks/useModule";
import { NotificationTypes } from "../../../components/Navbars/CloudNotifications/NotificationTypes";
import useReadNotifications from "../../../api/api-hooks/notifications/useReadNotifications";
import { callFunctionIfExists } from "../../../utils/GeneralUtils";
dayjs.extend(advancedFormat);

const QueryFields = [
  "Description",
  "Link",
  "Type",
  "Read",
  "Created",
  "Metadata.RecordName",
  "Metadata.RecordModule",
  "Metadata.Comment",
];

export function InitialNotificationQuery(): Query {
  return {
    Fields: QueryFields,
    OrderBy: { Field: "Created", Direction: "DESC" },
    Filters: [],
    Pagination: { Page: 1, Count: 25 },
  };
}

export const notificationIconMap: Record<NotificationTypes, string> = {
  "Comment Mention": "mdi mdi-at",
  "Comment Reply": "mdi mdi-reply",
  "Comment Reaction": "mdi mdi-emoticon-outline",
  "New Comment": "mdi mdi-comment-outline",
};

export default function NotificationsPage() {
  const [query, setQuery] = useState(InitialNotificationQuery());
  const [searchQuery, setSearchQuery] = useState<Query | null>(null);
  const { module: mod } = useModule("Notifications");
  const { notifications, pagination, loading } = useFetchNotifications(
    searchQuery || query,
    false
  );

  return (
    <div className="mt-4">
      <BasicToolbar
        query={query}
        setQuery={setQuery}
        mod={mod}
        onSearch={onSearch}
      />
      <NotificationsList loading={loading} notifications={notifications} />
      <div className="mx--3 mx-md--4">
        <AppTablePagination
          records={notifications}
          pagination={pagination}
          query={query ? query : searchQuery}
          setQuery={query ? setQuery : setSearchQuery}
          error={null}
          loading={loading}
          module={mod}
          sticky
        />
      </div>
    </div>
  );

  function onSearch(newSearch: string) {
    if (!newSearch) return setSearchQuery(null);

    setSearchQuery({
      Fields: QueryFields,
      Filters: [
        {
          Group: true,
          Filters: [
            { Field: "Description", Method: "contains", Value: newSearch },
          ],
        },
      ],
      Pagination: { Page: 1, Count: 25 },
    });
  }
}

export function NotificationsList({
  notifications,
  loading,
  onSelectNotification,
  small,
  maxWidth,
}: {
  notifications: NotificationRecord[];
  loading: boolean;
  onSelectNotification?: () => void;
  small?: boolean;
  maxWidth?: number | string;
}) {
  const navigate = useNavigate();
  const { readNotifications } = useReadNotifications();

  return (
    <BasicTableView
      containerCSS="mt-3 mb-3"
      title={undefined}
      loading={loading}
      items={notifications}
      emptyMessage="No Notifications found."
      getItemTitle={(n) =>
        small ? (
          <div className="text-truncate" style={{ fontSize: 13 }}>
            {n.Description}
          </div>
        ) : (
          <div style={{ fontSize: 14 }}>{n.Description}</div>
        )
      }
      getItemSubtitle={(n) => {
        return (
          <>
            {n?.Metadata?.Comment && (
              <div
                className={`text-truncate ${small ? "text-xs" : "text-sm"}`}
                style={{ maxWidth: maxWidth }}
              >
                {n?.Metadata?.Comment}
              </div>
            )}
            {n?.Metadata?.RecordName && (
              <div
                className="font-weight-600 text-truncate"
                style={{
                  fontSize: small ? 12 : undefined,
                }}
              >
                Conversation about{" "}
                <span className="text-primary font-weight-600">
                  {n?.Metadata?.RecordName}
                </span>
              </div>
            )}
            {!small && (
              <div className="font-weight-500">
                {dayjs(n.Created).format("MMMM Do YYYY, h:mm a")}
              </div>
            )}
          </>
        );
      }}
      getItemStyle={(n) => {
        return n?.Read === true
          ? ""
          : "bg-dropdown-selected border border-dropdown-selected-dark py-2";
      }}
      getItemIcon={(n) => (
        <i
          className={`${notificationIconMap[n.Type]} text-xl mr-3`}
          style={{ lineHeight: 1 }}
        />
      )}
      onClick={(n) => {
        navigate(n?.Link);

        // Mark all notifications with the SAME LINK as selected notification.
        // Support for Record Notifications and any other type of notifications.
        const notificationInfo = notifications.reduce(
          (acc, current) => {
            if (
              !n?.Read &&
              n?._id === current?._id &&
              n.Link === current.Link
            ) {
              acc.NotificationIDs.push(n._id);

              if (n.Metadata.Context === "Record") {
                if (!acc.RecordComments[current?.Metadata?.ContextID]) {
                  acc.RecordComments[current.Metadata.ContextID] = 0;
                }

                acc.RecordComments[current?.Metadata?.ContextID] += 1;
              }
            }

            return acc;
          },
          { NotificationIDs: [], RecordComments: {} } as {
            NotificationIDs: string[];
            RecordComments: { [key: string]: number };
          }
        );

        if (!n?.Read) {
          readNotifications({ notificationInfo: notificationInfo });
        }

        callFunctionIfExists(onSelectNotification);
      }}
    />
  );
}
